import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Image,
  Flex,
} from '@everlywell/ui-kit';
import { CaretDown, PlayCircle } from '@phosphor-icons/react';
import React, { useState } from 'react';

interface VideoData {
  id: string;
  thumbnail: string;
  poster: string;
  videoUrl: string;
  title: string;
  subtitle: string;
  sections: {
    title: string;
    content: string | string[];
  }[];
}

interface VideoGalleryProps {
  videoData: VideoData[];
}

const VideoGallery: React.FC<VideoGalleryProps> = ({ videoData }) => {
  const [playingVideoIndex, setPlayingVideoIndex] = useState<number | null>(
    null,
  );

  return (
    <Tabs
      orientation="vertical"
      variant="unstyled"
      width="100%"
      maxWidth="1440px"
      mx="auto"
      bg="#F9F9F6"
      borderRadius={['none', 'none', 'xl']}
      mt={[4, 4, 10]}
    >
      <Flex
        width="100%"
        gap={[4, 6, 8]}
        p={[2, 4, 8]}
        flexDirection={['column', 'column', 'row']}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          width={['100%', '100%', '60%']}
        >
          {/* Main Video */}
          <TabPanels>
            {videoData.map((video) => (
              <TabPanel key={video.id} p={0}>
                <Box
                  position="relative"
                  paddingTop="56.25%"
                  bg="gray.100"
                  borderRadius={['md', 'md', 'lg']}
                  overflow="hidden"
                  key={video.id}
                >
                  <Box
                    position="absolute"
                    w="100%"
                    h="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    top={0}
                    left={0}
                    onClick={() => {
                      const videos = document.querySelectorAll('video');
                      const currentIndex = videoData.findIndex(
                        (v) => v.videoUrl === video.videoUrl,
                      );
                      const currentVideo = videos[currentIndex];

                      videos.forEach((v) => {
                        if (v !== currentVideo) v.pause();
                      });

                      if (currentVideo.paused) {
                        currentVideo.play();
                        setPlayingVideoIndex(currentIndex);
                      } else {
                        currentVideo.pause();
                        setPlayingVideoIndex(null);
                      }
                    }}
                  >
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      bgGradient="linear(to-b, transparent, rgba(0,0,0,0.6))"
                      zIndex={1}
                      opacity={
                        playingVideoIndex ===
                        videoData.findIndex(
                          (v) => v.videoUrl === video.videoUrl,
                        )
                          ? 0
                          : 1
                      }
                      transition="opacity 0.2s"
                    />
                    <Flex
                      opacity={
                        playingVideoIndex ===
                        videoData.findIndex(
                          (v) => v.videoUrl === video.videoUrl,
                        )
                          ? 0
                          : 1
                      }
                      position="absolute"
                      bottom={0}
                      left={0}
                      w="100%"
                      p={[2, 3, 4]}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      zIndex={4}
                    >
                      <Box maxW={['85%', '80%', '72%']}>
                        <Text
                          color="white"
                          fontSize={['xl', 'xl', '2xl']}
                          fontWeight="bold"
                        >
                          {video.title}
                        </Text>
                        <Text
                          mt={1}
                          color="white"
                          zIndex={2}
                          fontSize={['sm', 'sm', 'md']}
                          fontWeight="medium"
                        >
                          {video.subtitle}
                        </Text>
                      </Box>
                      <Box>
                        <PlayCircle size={40} color="white" weight="fill" />
                      </Box>
                    </Flex>
                  </Box>
                  <Box
                    as="video"
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    preload="metadata"
                    src={video.videoUrl}
                    poster={video.poster}
                    borderRadius={['none', 'md', 'lg']}
                    playsInline
                    objectFit="cover"
                  />
                </Box>
              </TabPanel>
            ))}
          </TabPanels>

          {/* Video Thumbnails */}
          <TabList
            px={[0, 2, 3]}
            overflowX={['auto', 'auto', 'visible']}
            sx={{
              scrollbarWidth: 'none',
              '::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {videoData.map((video, index) => (
              <Tab
                key={video.id}
                p={0}
                minW={['120px', '150px', 'auto']}
                mr={index < videoData.length - 1 ? 2 : 0}
                _selected={{
                  border: '4px solid',
                  borderColor: '#28724F',
                  borderRadius: ['md', 'md', 'lg'],
                }}
              >
                <Image
                  src={video.thumbnail}
                  alt={`${video.title} thumbnail`}
                  width="100%"
                  height={['67px', '84px', 'auto']}
                  borderRadius={['md', 'md', 'lg']}
                  objectFit="cover"
                />
              </Tab>
            ))}
          </TabList>
        </Box>
        <Box width={['100%', '100%', '40%']}>
          {/* Right Column - Information */}
          <TabPanels>
            {videoData.map((video) => (
              <TabPanel key={video.id} p={0}>
                <Text
                  fontSize="md"
                  mb={[2, 2, 4]}
                  color="#28724F"
                  mt={[2, 3, 4]}
                  pl={[2, 3, 4]}
                >
                  How did it work for {video.title}?
                </Text>
                <Accordion allowToggle>
                  {video.sections.map((section, index) => (
                    <AccordionItem
                      key={index}
                      borderTop={index === 0 ? 'none' : '1px solid'}
                      borderBottom="1px solid"
                      borderColor="#E8E8E2"
                    >
                      {({ isExpanded }) => (
                        <>
                          <AccordionButton px={[2, 3, 4]} py={[3, 3, 4]}>
                            <Text
                              flex="1"
                              textAlign="left"
                              fontWeight="medium"
                              fontSize={['sm', 'md', 'md']}
                            >
                              {section.title}
                            </Text>
                            <Box
                              transition="transform 0.2s"
                              transform={
                                isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                              }
                            >
                              <CaretDown size={20} />
                            </Box>
                          </AccordionButton>
                          <AccordionPanel pb={[3, 3, 4]} px={[2, 3, 4]}>
                            {Array.isArray(section.content) ? (
                              <Box as="ul" pl={4}>
                                {section.content.map((item, i) => (
                                  <Box
                                    as="li"
                                    key={i}
                                    mb={2}
                                    listStyleType="disc"
                                    sx={{ '&::marker': { fontSize: '0.8em' } }}
                                  >
                                    <Text fontSize={['sm', 'sm', 'md']}>
                                      {item}
                                    </Text>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Text fontSize={['sm', 'sm', 'md']}>
                                {section.content}
                              </Text>
                            )}
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>
                  ))}
                </Accordion>
              </TabPanel>
            ))}
          </TabPanels>
        </Box>
      </Flex>
    </Tabs>
  );
};

export default VideoGallery;
